import React from 'react';

interface Field {
    system_name: string;
    display_name: string;
    field_type: string;
    options?: string[];
}

interface DraftIssue {
    [key: string]: any;
    confidence?: { [key: string]: number };
    rationale?: { [key: string]: string };
}

interface DraftIssueCardProps {
    draftIssue: DraftIssue;
    onFieldChange: (field: string, value: string) => void;
    fields: Field[];
    status?: 'draft' | 'submitted';
    issueId?: string;
    onViewDraft?: () => void;
}

const DraftIssueCard = ({ draftIssue, onFieldChange, fields, status = 'draft', issueId, onViewDraft }: DraftIssueCardProps) => {
    const isDraft = status === 'draft';
    const isSubmitted = status === 'submitted';
    return (
        <div
            className={`draft-issue-card rounded-xl border shadow px-6 py-4 my-2 ${
                isDraft
                    ? 'bg-orange-100 text-orange-900 border-orange-300'
                    : isSubmitted
                    ? 'bg-green-100 text-green-900 border-green-300'
                    : 'bg-gray-100 text-gray-900 border-gray-200'
            }`}
        >
            <div className="flex items-center mb-3 justify-between">
                <span className={`font-bold text-lg mr-2 ${isDraft ? 'text-orange-700' : isSubmitted ? 'text-green-700' : ''}`}>
                    {isDraft ? 'Draft Issue' : 'Submitted Issue'}
                </span>
                {isDraft && onViewDraft && (
                    <button
                        onClick={onViewDraft}
                        className="ml-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >
                        View Issue
                    </button>
                )}
                {isSubmitted && issueId && (
                    <a
                        href={`/issues/${issueId}`}
                        className="ml-2 text-green-800 underline text-sm hover:text-green-900"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        View Issue
                    </a>
                )}
            </div>
            {isSubmitted && (
                <div className="font-semibold text-lg mb-2">
                    {draftIssue.title || draftIssue.name || 'Untitled Issue'}
                </div>
            )}
            {fields.map((field) => (
                <div key={field.system_name} className="field mb-2">
                    <label className="block font-medium mb-1">{field.display_name}</label>
                    <div className="field-value">
                        {field.field_type === 'select' ? (
                            <select
                                value={draftIssue[field.system_name] || ''}
                                onChange={(e) => onFieldChange(field.system_name, e.target.value)}
                                disabled={isSubmitted}
                                className="rounded border px-2 py-1"
                            >
                                <option value="">Select...</option>
                                {field.options?.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={draftIssue[field.system_name] || ''}
                                onChange={(e) => onFieldChange(field.system_name, e.target.value)}
                                disabled={isSubmitted}
                                className="rounded border px-2 py-1 w-full"
                            />
                        )}
                    </div>
                    {draftIssue[field.system_name] && (
                        <div className="field-confidence text-xs mt-1">
                            Confidence: {Math.round((draftIssue.confidence?.[field.system_name] || 0) * 100)}%
                        </div>
                    )}
                    {draftIssue.rationale?.[field.system_name] && (
                        <div className="field-rationale text-xs text-gray-600 italic mt-1">
                            Rationale: {draftIssue.rationale[field.system_name]}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default DraftIssueCard; 